import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkle, PaperPlaneRight, Leaf } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Should I start a SIP or clear my credit card first?",
  "How much should I keep as an emergency fund?",
  "Where am I overspending this month?",
  "Old vs new tax regime — which suits me?",
];

function TypingDots() {
  return (
    <div className="flex gap-1.5 items-center py-1">
      <span className="organic-dot h-2 w-2 rounded-full bg-primary" />
      <span className="organic-dot h-2 w-2 rounded-full bg-primary" />
      <span className="organic-dot h-2 w-2 rounded-full bg-primary" />
    </div>
  );
}

export default function Copilot() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(`sess-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + payload.delta,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-screen relative">
      <div className="grain absolute inset-0 opacity-[0.04] pointer-events-none" />

      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center gap-3 relative z-10">
        <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
          <Sparkle size={18} weight="fill" className="text-primary-foreground" />
        </div>
        <div>
          <h1 className="font-heading font-semibold text-lg leading-tight">Invesy Assistant</h1>
          <p className="text-xs text-muted-foreground">Powered by GPT-5.4 · knows your numbers</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {empty ? (
            <div className="text-center py-10">
              <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-5">
                <Leaf size={28} weight="fill" className="text-primary-foreground" />
              </div>
              <h2 className="font-heading font-bold text-2xl tracking-tight mb-2">
                How can I help with your money today?
              </h2>
              <p className="text-muted-foreground mb-8">
                Ask me anything — I use your income, spends and goals to answer.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => send(s)}
                    className="rounded-xl border border-border bg-card p-4 text-sm hover:border-primary/50 hover:bg-secondary/40 transition-colors duration-200 text-left"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <AnimatePresence initial={false}>
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      data-testid={`chat-msg-${m.role}`}
                      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground rounded-2xl rounded-tr-sm"
                          : "bg-card border border-border rounded-2xl rounded-tl-sm"
                      }`}
                    >
                      {m.content ? (
                        m.content
                      ) : streaming ? (
                        <TypingDots />
                      ) : (
                        ""
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-4 relative z-10">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <Textarea
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask about SIPs, EMIs, savings, taxes…"
            rows={1}
            className="resize-none rounded-2xl p-4 min-h-[52px] max-h-40 bg-card"
          />
          <Button
            data-testid="copilot-send"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="h-[52px] w-[52px] rounded-2xl bg-primary hover:bg-primary/90 shrink-0"
          >
            <PaperPlaneRight size={20} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
}
